import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Package, MapPin, User, Phone, FileText, AlertCircle, Loader, Key, Copy, CheckCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';

const BookDelivery = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pricing, setPricing] = useState(null);
  
  // OTP Modal state
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otpData, setOtpData] = useState(null);
  
  // Get saved locations from home page
  const [pickup, setPickup] = useState('');
  const [dropoff, setDropoff] = useState('');
  
  // Package details
  const [packageSize, setPackageSize] = useState('small');
  const [packageWeight, setPackageWeight] = useState('');
  const [packageDescription, setPackageDescription] = useState('');
  const [isFragile, setIsFragile] = useState(false);
  const [specialInstructions, setSpecialInstructions] = useState('');
  
  // Receiver details
  const [receiverName, setReceiverName] = useState('');
  const [receiverPhone, setReceiverPhone] = useState('');
  const [receiverEmail, setReceiverEmail] = useState('');
  const [receiverAlternatePhone, setReceiverAlternatePhone] = useState('');
  
  // Payment
  const [paymentMethod, setPaymentMethod] = useState('cash');

  useEffect(() => {
    // Get saved pickup/dropoff from localStorage
    const savedPickup = localStorage.getItem('ride_pickup');
    const savedDropoff = localStorage.getItem('ride_dropoff');
    
    if (savedPickup) setPickup(savedPickup);
    if (savedDropoff) setDropoff(savedDropoff);
    
    // Fetch pricing info
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await api.get('/deliveries/pricing');
      setPricing(response.data.pricing);
    } catch (error) {
      console.error('Error fetching pricing:', error);
    }
  };

  const handleBookDelivery = async () => {
    // Validation
    if (!pickup || !dropoff) {
      toast({
        title: 'Missing locations',
        description: 'Please enter both pickup and dropoff locations',
        variant: 'destructive',
      });
      return;
    }

    if (!packageDescription) {
      toast({
        title: 'Package description required',
        description: 'Please describe what you\'re sending',
        variant: 'destructive',
      });
      return;
    }

    if (!receiverName || !receiverPhone) {
      toast({
        title: 'Receiver details required',
        description: 'Please provide receiver name and phone number',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      const deliveryData = {
        pickupLocation: pickup,
        dropoffLocation: dropoff,
        packageDetails: {
          size: packageSize,
          weight: packageWeight ? parseFloat(packageWeight) : null,
          description: packageDescription,
          fragile: isFragile,
          specialInstructions: specialInstructions || null,
        },
        receiverDetails: {
          name: receiverName,
          phone: receiverPhone,
          email: receiverEmail || null,
          alternatePhone: receiverAlternatePhone || null,
        },
        paymentMethod: paymentMethod,
      };

      const response = await api.post('/deliveries/book', deliveryData);

      if (response.data.success) {
        // Store OTP data for display
        setOtpData({
          deliveryId: response.data.delivery.id,
          pickupOTP: response.data.pickupOTP,
          deliveryOTP: response.data.deliveryOTP,
          receiverName: receiverName,
          receiverPhone: receiverPhone,
        });
        
        // Show OTP modal
        setShowOTPModal(true);
        
        toast({
          title: 'Delivery booked!',
          description: `Your package will be picked up soon. Delivery ID: ${response.data.delivery.id}`,
        });

        // Clear localStorage
        localStorage.removeItem('ride_pickup');
        localStorage.removeItem('ride_dropoff');
        localStorage.removeItem('booking_mode');
      }
    } catch (error) {
      toast({
        title: 'Booking failed',
        description: error.response?.data?.detail || 'Failed to book delivery',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const sizeMultiplier = pricing?.sizeMultipliers?.[packageSize] || 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-4"
          >
            ← Back to home
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Send a Package</h1>
          <p className="text-gray-600 mt-2">Fill in the details to book your delivery</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Locations */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-pink-600" />
                Pickup & Delivery
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pickup Location
                  </label>
                  <Input
                    value={pickup}
                    onChange={(e) => setPickup(e.target.value)}
                    placeholder="Enter pickup location"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Delivery Location
                  </label>
                  <Input
                    value={dropoff}
                    onChange={(e) => setDropoff(e.target.value)}
                    placeholder="Enter delivery location"
                  />
                </div>
              </div>
            </Card>

            {/* Package Details */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Package className="h-5 w-5 text-pink-600" />
                Package Details
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Package Size
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {['small', 'medium', 'large'].map((size) => (
                      <button
                        key={size}
                        onClick={() => setPackageSize(size)}
                        className={`py-3 px-4 rounded-lg font-medium transition-all border ${
                          packageSize === size
                            ? 'bg-pink-600 text-white border-pink-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-pink-300'
                        }`}
                      >
                        {size.charAt(0).toUpperCase() + size.slice(1)}
                      </button>
                    ))}
                  </div>
                  {pricing && (
                    <p className="text-xs text-gray-600 mt-2">
                      {pricing.description[packageSize]}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Weight (kg) - Optional
                  </label>
                  <Input
                    type="number"
                    step="0.1"
                    value={packageWeight}
                    onChange={(e) => setPackageWeight(e.target.value)}
                    placeholder="e.g., 2.5"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Package Description *
                  </label>
                  <Input
                    value={packageDescription}
                    onChange={(e) => setPackageDescription(e.target.value)}
                    placeholder="e.g., Documents, Electronics, Clothing"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="fragile"
                    checked={isFragile}
                    onChange={(e) => setIsFragile(e.target.checked)}
                    className="h-4 w-4 text-pink-600 rounded"
                  />
                  <label htmlFor="fragile" className="text-sm text-gray-700">
                    Fragile - Handle with care
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Special Instructions (Optional)
                  </label>
                  <textarea
                    value={specialInstructions}
                    onChange={(e) => setSpecialInstructions(e.target.value)}
                    placeholder="Any special handling instructions..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    rows={3}
                  />
                </div>
              </div>
            </Card>

            {/* Receiver Details */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <User className="h-5 w-5 text-pink-600" />
                Receiver Details
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Receiver Name *
                  </label>
                  <Input
                    value={receiverName}
                    onChange={(e) => setReceiverName(e.target.value)}
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Receiver Phone *
                  </label>
                  <Input
                    value={receiverPhone}
                    onChange={(e) => setReceiverPhone(e.target.value)}
                    placeholder="+260 XXX XXXX"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Receiver Email (Optional - for OTP)
                  </label>
                  <Input
                    type="email"
                    value={receiverEmail}
                    onChange={(e) => setReceiverEmail(e.target.value)}
                    placeholder="receiver@example.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    If provided, delivery OTP will be sent directly to receiver
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alternate Phone (Optional)
                  </label>
                  <Input
                    value={receiverAlternatePhone}
                    onChange={(e) => setReceiverAlternatePhone(e.target.value)}
                    placeholder="+260 XXX XXXX"
                  />
                </div>
              </div>
            </Card>

            {/* Payment Method */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Payment Method</h2>
              <div className="space-y-3">
                {['cash', 'card', 'wallet'].map((method) => (
                  <label
                    key={method}
                    className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all ${
                      paymentMethod === method
                        ? 'border-pink-600 bg-pink-50'
                        : 'border-gray-300 hover:border-pink-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="payment"
                      value={method}
                      checked={paymentMethod === method}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="h-4 w-4 text-pink-600"
                    />
                    <span className="ml-3 font-medium capitalize">{method}</span>
                  </label>
                ))}
              </div>
            </Card>
          </div>

          {/* Sidebar - Price Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              <Card className="p-6">
                <h2 className="text-xl font-semibold mb-4">Delivery Summary</h2>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Package Size</span>
                    <span className="font-medium capitalize">{packageSize}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Price Multiplier</span>
                    <span className="font-medium">{sizeMultiplier}x</span>
                  </div>
                  {isFragile && (
                    <div className="flex items-center gap-2 text-sm text-orange-600">
                      <AlertCircle className="h-4 w-4" />
                      <span>Fragile package</span>
                    </div>
                  )}
                </div>

                <div className="bg-pink-50 p-4 rounded-lg mb-6">
                  <p className="text-sm text-gray-600 mb-1">Estimated Price</p>
                  <p className="text-xs text-gray-500">
                    (Calculated after booking based on distance)
                  </p>
                </div>

                <div className="space-y-2 text-xs text-gray-600 mb-6">
                  <p>• Only Motor Bike drivers will deliver your package</p>
                  <p>• Commission: 20% of delivery price</p>
                  <p>• Real-time tracking available</p>
                  <p>• Proof of delivery with photo</p>
                </div>

                <Button
                  onClick={handleBookDelivery}
                  disabled={loading}
                  className="w-full bg-pink-600 hover:bg-pink-700 text-white py-6"
                  data-testid="book-delivery-btn"
                >
                  {loading ? (
                    <>
                      <Loader className="animate-spin h-5 w-5 mr-2" />
                      Booking...
                    </>
                  ) : (
                    'Book Delivery'
                  )}
                </Button>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* OTP Display Modal */}
      {showOTPModal && otpData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6 bg-white" data-testid="otp-modal">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Delivery Booked!</h2>
              <p className="text-gray-600 mt-2">Save these OTP codes for verification</p>
            </div>

            {/* Pickup OTP */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Key className="h-5 w-5 text-blue-600" />
                <span className="font-semibold text-blue-800">Pickup OTP (For You)</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-3xl font-mono font-bold text-blue-900" data-testid="pickup-otp">
                  {otpData.pickupOTP?.code}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(otpData.pickupOTP?.code);
                    toast({ title: 'Copied!', description: 'Pickup OTP copied to clipboard' });
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-blue-700 mt-2">
                Give this code to the biker when they arrive for pickup
              </p>
            </div>

            {/* Delivery OTP */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Key className="h-5 w-5 text-purple-600" />
                <span className="font-semibold text-purple-800">Delivery OTP (For Receiver)</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-3xl font-mono font-bold text-purple-900" data-testid="delivery-otp">
                  {otpData.deliveryOTP?.code}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(otpData.deliveryOTP?.code);
                    toast({ title: 'Copied!', description: 'Delivery OTP copied to clipboard' });
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-purple-700 mt-2">
                Share this with <strong>{otpData.receiverName}</strong> ({otpData.receiverPhone})
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
              <p className="text-sm text-yellow-800">
                <AlertCircle className="h-4 w-4 inline mr-1" />
                OTPs expire in 60 minutes. You can view them again in your delivery history.
              </p>
            </div>

            <Button
              onClick={() => {
                setShowOTPModal(false);
                navigate('/deliveries');
              }}
              className="w-full bg-pink-600 hover:bg-pink-700 text-white"
              data-testid="close-otp-modal-btn"
            >
              Continue to My Deliveries
            </Button>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BookDelivery;
